declare const process: {
    env: {
        MSGAUTO_INBOX_SPA_CLIENT_ID?: string;
        MSGAUTO_INBOX_API_BASE?: string;
        MSGAUTO_INBOX_TENANT_ID?: string;
        MSGAUTO_INBOX_SCOPE?: string;
    };
};

export type InboxEmbedConfig = {
    apiBase: string;
    clientId: string;
    tenantId: string;
    scope: string;
};

const QA_TENANT = "6baa4eb1-106a-440b-b4f6-50134497d36d";
const PROD_TENANT = "5de110f8-2e0f-4d45-891d-bcf2218e253d";
const QA_SCOPE = "api://msgauto.namqa.corpqa.geuc.corp.gm.com/.default";
const PROD_SCOPE = "api://msgauto.nam.corp.gm.com/.default";

function env(value: string | undefined): string {
    if (value == null || value === "undefined") {
        return "";
    }
    return String(value).trim();
}

/**
 * Read bake-time values via static `process.env.NAME` access so webpack DefinePlugin
 * can inline them. Dynamic `process.env[name]` is left as a runtime `process` lookup
 * and throws `process is not defined` in the browser.
 */
function bakedEnv(): { spaClientId: string; api: string; tenant: string; scope: string } {
    return {
        spaClientId: env(process.env.MSGAUTO_INBOX_SPA_CLIENT_ID),
        api: env(process.env.MSGAUTO_INBOX_API_BASE).replace(/\/$/, ""),
        tenant: env(process.env.MSGAUTO_INBOX_TENANT_ID),
        scope: env(process.env.MSGAUTO_INBOX_SCOPE)
    };
}

/** Defaults from bake-time env, then script host, then localhost API. */
export function configFromScriptSrc(scriptSrc: string): InboxEmbedConfig {
    const src = (scriptSrc || "").toLowerCase();
    const { spaClientId, api: bakedApi, tenant: bakedTenant, scope: bakedScope } = bakedEnv();

    let inferred: InboxEmbedConfig = {
        apiBase: "https://localhost:5001",
        clientId: spaClientId,
        tenantId: QA_TENANT,
        scope: QA_SCOPE
    };

    if (src.includes("messageautomation.gm.com") || src.includes("msgauto.musea2.azure.ext.gm.com") || src.includes("msgauto-api.musea2")) {
        inferred = {
            apiBase: "https://msgauto-api.musea2.azure.ext.gm.com",
            clientId: spaClientId,
            tenantId: PROD_TENANT,
            scope: PROD_SCOPE
        };
    } else if (src.includes("messageautomation-test.gm.com") || src.includes("msgauto-tst.musea2") || src.includes("msgauto-api-tst")) {
        inferred = {
            apiBase: "https://msgauto-api-tst.musea2.azure.ext.gm.com",
            clientId: spaClientId,
            tenantId: QA_TENANT,
            scope: QA_SCOPE
        };
    } else if (src.includes("messageautomation-dev.gm.com") || src.includes("msgauto-dev.musea2") || src.includes("msgauto-api-dev")) {
        inferred = {
            apiBase: "https://msgauto-api-dev.musea2.azure.ext.gm.com",
            clientId: spaClientId,
            tenantId: QA_TENANT,
            scope: QA_SCOPE
        };
    }

    return {
        apiBase: bakedApi || inferred.apiBase,
        clientId: spaClientId,
        tenantId: bakedTenant || inferred.tenantId,
        scope: bakedScope || inferred.scope
    };
}

export function attr(script: HTMLScriptElement | null, name: string): string {
    return (script?.getAttribute(name) || "").trim();
}

export function resolveEmbedConfig(
    script: HTMLScriptElement | null,
    scriptSrc = script?.src || ""
): InboxEmbedConfig {
    const baked = configFromScriptSrc(scriptSrc);
    return {
        apiBase: (attr(script, "data-api") || baked.apiBase).replace(/\/$/, ""),
        clientId: attr(script, "data-client-id") || baked.clientId,
        tenantId: attr(script, "data-tenant-id") || baked.tenantId,
        scope: attr(script, "data-scope") || baked.scope
    };
}
